import { useEffect, useCallback, useRef, useState } from 'react';
import socketService, {
  SOCKET_EMIT_EVENTS,
  OrderEventData,
  SocketEventCallback
} from '../services/socket.service';
import { useSocketContext } from '../contexts/SocketContext';

/**
 * Hook để lấy trạng thái socket connection từ context
 * Connection được quản lý tập trung ở SocketProvider (Layout)
 */
export function useSocketConnection() {
  const { isConnected, isConnecting, error, reconnect } = useSocketContext();

  return {
    isConnected,
    isConnecting,
    error,
    reconnect,
  };
}

/**
 * Hook để subscribe order events
 */
export function useOrderEvents(
  onOrderCreated?: SocketEventCallback,
  onOrderStatusChanged?: SocketEventCallback,
  onOrderCancelled?: SocketEventCallback,
  onPaymentSuccess?: SocketEventCallback,
  onNewOrderForPickup?: SocketEventCallback,
  onNewOrderForLaundry?: SocketEventCallback,
  onNewOrderForDelivery?: SocketEventCallback,
  onOrderAssigned?: SocketEventCallback,
  onEquipmentAssigned?: SocketEventCallback,
  onEquipmentReleased?: SocketEventCallback,
  onOrderStepAdvanced?: SocketEventCallback,
  onOrderSplit?: SocketEventCallback,
) {
  const callbacksRef = useRef({
    onOrderCreated,
    onOrderStatusChanged,
    onOrderCancelled,
    onPaymentSuccess,
    onNewOrderForPickup,
    onNewOrderForLaundry,
    onNewOrderForDelivery,
    onOrderAssigned,
    onEquipmentAssigned,
    onEquipmentReleased,
    onOrderStepAdvanced,
    onOrderSplit,
  });

  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onOrderCreated,
      onOrderStatusChanged,
      onOrderCancelled,
      onPaymentSuccess,
      onNewOrderForPickup,
      onNewOrderForLaundry,
      onNewOrderForDelivery,
      onOrderAssigned,
      onEquipmentAssigned,
      onEquipmentReleased,
      onOrderStepAdvanced,
      onOrderSplit,
    };
  }, [
    onOrderCreated,
    onOrderStatusChanged,
    onOrderCancelled,
    onPaymentSuccess,
    onNewOrderForPickup,
    onNewOrderForLaundry,
    onNewOrderForDelivery,
    onOrderAssigned,
    onEquipmentAssigned,
    onEquipmentReleased,
    onOrderStepAdvanced,
    onOrderSplit,
  ]);

  useEffect(() => {
    const handlers: { event: string; handler: SocketEventCallback }[] = [];

    // Order created
    if (callbacksRef.current.onOrderCreated) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onOrderCreated?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.ORDER_CREATED, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.ORDER_CREATED, handler });
    }

    // Order status changed
    if (callbacksRef.current.onOrderStatusChanged) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onOrderStatusChanged?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.ORDER_STATUS_CHANGED, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.ORDER_STATUS_CHANGED, handler });
    }

    // Order cancelled
    if (callbacksRef.current.onOrderCancelled) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onOrderCancelled?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.ORDER_CANCELLED, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.ORDER_CANCELLED, handler });
    }

    // Payment success
    if (callbacksRef.current.onPaymentSuccess) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onPaymentSuccess?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.ORDER_PAYMENT_SUCCESS, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.ORDER_PAYMENT_SUCCESS, handler });
    }

    // New order for pickup
    if (callbacksRef.current.onNewOrderForPickup) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onNewOrderForPickup?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.NEW_ORDER_FOR_PICKUP, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.NEW_ORDER_FOR_PICKUP, handler });
    }

    // New order for laundry
    if (callbacksRef.current.onNewOrderForLaundry) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onNewOrderForLaundry?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.NEW_ORDER_FOR_LAUNDRY, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.NEW_ORDER_FOR_LAUNDRY, handler });
    }

    // New order for delivery
    if (callbacksRef.current.onNewOrderForDelivery) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onNewOrderForDelivery?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.NEW_ORDER_FOR_DELIVERY, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.NEW_ORDER_FOR_DELIVERY, handler });
    }

    // Order assigned (khi gắn nhân viên vào đơn hàng)
    if (callbacksRef.current.onOrderAssigned) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onOrderAssigned?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.ORDER_ASSIGNED, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.ORDER_ASSIGNED, handler });
    }

    // Equipment assigned (khi gắn thiết bị vào đơn hàng)
    if (callbacksRef.current.onEquipmentAssigned) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onEquipmentAssigned?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.EQUIPMENT_ASSIGNED, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.EQUIPMENT_ASSIGNED, handler });
    }

    // Equipment released (khi trả thiết bị khỏi đơn hàng)
    if (callbacksRef.current.onEquipmentReleased) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onEquipmentReleased?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.EQUIPMENT_RELEASED, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.EQUIPMENT_RELEASED, handler });
    }

    // Order step advanced (Phase 2 - extended workflow step transition)
    if (callbacksRef.current.onOrderStepAdvanced) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onOrderStepAdvanced?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.ORDER_STEP_ADVANCED, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.ORDER_STEP_ADVANCED, handler });
    }

    // Order split (Phase 2 - order split into sub-orders)
    if (callbacksRef.current.onOrderSplit) {
      const handler: SocketEventCallback = (data) => callbacksRef.current.onOrderSplit?.(data);
      socketService.on(SOCKET_EMIT_EVENTS.ORDER_SPLIT, handler);
      handlers.push({ event: SOCKET_EMIT_EVENTS.ORDER_SPLIT, handler });
    }

    // Cleanup
    return () => {
      handlers.forEach(({ event, handler }) => {
        socketService.off(event, handler);
      });
    };
  }, []);
}

/**
 * Hook để subscribe đơn hàng của các chi nhánh
 */
export function useOrderSubscription(branchIds: number[]) {
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!branchIds.length) return;

    const subscribe = () => {
      if (socketService.isConnected()) {
        socketService.subscribeToOrders(branchIds);
        setIsSubscribed(true);
      }
    };

    // Subscribe immediately if connected
    subscribe();

    // Also subscribe after reconnection
    const checkConnection = setInterval(() => {
      if (socketService.isConnected() && !isSubscribed) {
        subscribe();
      }
    }, 2000);

    return () => {
      clearInterval(checkConnection);
    };
  }, [branchIds, isSubscribed]);

  return { isSubscribed };
}

/**
 * Hook để lắng nghe notification
 */
export function useNotification(onNotification: (data: OrderEventData) => void) {
  const callbackRef = useRef(onNotification);

  useEffect(() => {
    callbackRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    const handler: SocketEventCallback = (data) => {
      callbackRef.current(data);
    };

    socketService.on(SOCKET_EMIT_EVENTS.NOTIFICATION, handler);

    return () => {
      socketService.off(SOCKET_EMIT_EVENTS.NOTIFICATION, handler);
    };
  }, []);
}

/**
 * Combined hook for order list pages
 * Connection được quản lý tập trung ở SocketProvider (Layout)
 * Subscription được quản lý ở BranchContext (BranchProvider)
 * Hook này CHỈ lắng nghe events, không subscribe (để tránh duplicate)
 *
 * @param branchIds - Không còn dùng để subscribe, chỉ để tương thích API cũ
 * @param callbacks - Các callback xử lý events
 */
export function useOrderRealtime(
  branchIds: number[],
  callbacks: {
    onOrderCreated?: SocketEventCallback;
    onOrderStatusChanged?: SocketEventCallback;
    onOrderCancelled?: SocketEventCallback;
    onNewOrderForPickup?: SocketEventCallback;
    onNewOrderForLaundry?: SocketEventCallback;
    onNewOrderForDelivery?: SocketEventCallback;
    onOrderAssigned?: SocketEventCallback;
    onEquipmentAssigned?: SocketEventCallback;
    onEquipmentReleased?: SocketEventCallback;
    onOrderStepAdvanced?: SocketEventCallback;
    onOrderSplit?: SocketEventCallback;
  }
) {
  // Lấy trạng thái connection từ context (không tự connect)
  const { isConnected, isConnecting, error } = useSocketContext();

  // Subscription được quản lý bởi BranchContext, không cần gọi useOrderSubscription ở đây
  // BranchContext đã subscribe dựa trên selectedBranches khi user chọn chi nhánh
  // Nếu cần biết isSubscribed, lấy từ BranchContext thay vì gọi useOrderSubscription
  const isSubscribed = isConnected && branchIds.length > 0;

  useOrderEvents(
    callbacks.onOrderCreated,
    callbacks.onOrderStatusChanged,
    callbacks.onOrderCancelled,
    undefined, // payment success
    callbacks.onNewOrderForPickup,
    callbacks.onNewOrderForLaundry,
    callbacks.onNewOrderForDelivery,
    callbacks.onOrderAssigned,
    callbacks.onEquipmentAssigned,
    callbacks.onEquipmentReleased,
    callbacks.onOrderStepAdvanced,
    callbacks.onOrderSplit,
  );

  return {
    isConnected,
    isConnecting,
    isSubscribed,
    error,
  };
}

export default {
  useSocketConnection,
  useOrderEvents,
  useOrderSubscription,
  useNotification,
  useOrderRealtime,
};
